/**
 * Checklist del expediente con optimistic UI + manejo de documentos
 * adjuntos por item (Sprint 5 + Sprint 10 · 2 + Sprint 11 fix).
 *
 * El sub-componente `ChecklistItemDocument` se queda co-localizado
 * acá porque solo se usa desde este orchestrator y comparte el
 * contrato del item.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";

import { InlineSpinner } from "@/components/InlineSpinner";
import { track } from "@/lib/analytics";
import {
  ApiError,
  deleteProjectDocument,
  downloadProjectDocument,
  toggleChecklistItem,
  uploadProjectDocument,
  type ChecklistItemDTO,
  type ProjectDetailDTO,
} from "@/lib/api";
import { useActiveMunicipalityDane } from "@/lib/municipality";
import { CHECKLIST_ANCHOR_ID } from "./focus";

export function ChecklistSection({
  project,
  onToggled,
}: {
  project: ProjectDetailDTO;
  onToggled: () => void;
}) {
  const activeDane = useActiveMunicipalityDane();
  const queryClient = useQueryClient();

  /**
   * Optimistic UI (Sprint 11 fix): el checkbox antes esperaba al
   * roundtrip + refetch antes de pintar el cambio. Con conexiones
   * lentas se sentía 800-1500ms de "click muerto".
   *
   * Ahora:
   * 1. `onMutate` parchea el cache de TanStack Query con el
   *    `is_completed` nuevo → la UI cambia instantáneo.
   * 2. Si el backend acepta, `onSuccess` invalida y trae datos
   *    frescos (puede traer otros campos como `linked_document_id`
   *    actualizados).
   * 3. Si el backend rechaza, `onError` revierte al estado previo
   *    usando el snapshot.
   *
   * Quitamos `disabled={mutation.isPending}` para que múltiples
   * checks rápidos no se trabe entre sí — cada toggle es su propia
   * mutación.
   */
  const mutation = useMutation({
    mutationFn: ({ item, value }: { item: ChecklistItemDTO; value: boolean }) =>
      toggleChecklistItem(project.id, item.id, activeDane, value),
    onMutate: async ({ item, value }) => {
      const queryKey = ["project-detail", project.id];
      // Cancelamos refetches en vuelo para no pisar el optimistic update.
      await queryClient.cancelQueries({ queryKey });
      const snapshot = queryClient.getQueryData<ProjectDetailDTO>(queryKey);
      queryClient.setQueryData<ProjectDetailDTO>(queryKey, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          checklist: prev.checklist.map((c) =>
            c.id === item.id ? { ...c, is_completed: value } : c,
          ),
        };
      });
      return { snapshot, queryKey };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) {
        queryClient.setQueryData(ctx.queryKey, ctx.snapshot);
      }
    },
    onSettled: () => {
      // Refetch al final — pase o no pase la mutación. Si pasó,
      // sincroniza con el backend (puede traer cambios indirectos).
      // Si falló, también para que el cache refleje el estado real.
      onToggled();
    },
  });

  const total = project.checklist.length;
  const completed = project.checklist.filter((i) => i.is_completed).length;

  return (
    <section
      id={CHECKLIST_ANCHOR_ID}
      style={{ scrollMarginTop: "5rem" }}
      aria-label="Checklist"
      data-testid="checklist"
      data-tour-id="checklist"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs"
    >
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">Checklist de radicación</h2>
        {total > 0 && (
          <span className="text-xs text-slate-500">
            {completed} de {total} completados
          </span>
        )}
      </div>
      {total === 0 && (
        // Issue #569: los expedientes idea-first nacen sin checklist por
        // diseno (la identificacion va por la formulacion). "0 de 0
        // completados" sin un solo item parecia un bug; el vacio dice que
        // pasa y que sigue.
        <p data-testid="checklist-empty" className="text-sm text-slate-600">
          {project.opportunity_id === null
            ? vacioIdeaFirst(project)
            : "Este expediente no tiene requisitos documentales cargados."}
        </p>
      )}
      <ul className="space-y-2">
        {project.checklist.map((item) => {
          const blockedNeedsUpload = item.is_required && !item.linked_document_id;
          return (
            <li
              key={item.id}
              className="flex items-start gap-3 rounded-lg border border-slate-100 bg-slate-50 p-3"
            >
              <input
                type="checkbox"
                aria-label={item.title}
                checked={item.is_completed}
                disabled={blockedNeedsUpload}
                title={blockedNeedsUpload ? "Sube el archivo primero" : undefined}
                onChange={(e) => mutation.mutate({ item, value: e.target.checked })}
                className="mt-0.5 h-4 w-4 cursor-pointer rounded border-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
              />
              <div className="flex-1">
                <p
                  className={
                    "text-sm font-semibold " +
                    (item.is_completed ? "text-slate-500 line-through" : "text-slate-900")
                  }
                >
                  {item.title}
                  {!item.is_required && (
                    <span className="ml-2 text-[10px] font-normal tracking-wide text-slate-400 uppercase">
                      opcional
                    </span>
                  )}
                </p>
                {item.description && <p className="text-xs text-slate-600">{item.description}</p>}
                <ChecklistItemDocument
                  item={item}
                  projectId={project.id}
                  dane={activeDane}
                  onChanged={onToggled}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * Vacio del checklist de un idea-first segun el estado (issue #881): el
 * "Cuando lo radiques" solo tiene sentido en borrador. Radicado sin BPIN
 * usa la misma regla del banner (`bpin_pending`, del backend).
 */
function vacioIdeaFirst(project: ProjectDetailDTO): string {
  const base = "Este expediente no tiene checklist documental: se radica con la formulación.";
  if (project.status === "draft") return `${base} Cuando lo radiques en PIIP, registra el BPIN.`;
  if (project.bpin_pending) return `${base} Ya está radicado: registra el BPIN que te asignó PIIP.`;
  return base;
}

/**
 * Sub-componente que maneja el upload + display + delete del documento
 * adjunto a un checklist item. Sprint 10 · 2.
 *
 * Estados:
 * - Sin doc: muestra botón "Adjuntar documento" → input file.
 * - Con doc: muestra filename + botón download + botón delete.
 *
 * El upload triggers `onChanged` (refetch del project detail) para
 * que el padre reciba el is_completed actualizado del backend.
 */
function ChecklistItemDocument({
  item,
  projectId,
  dane,
  onChanged,
}: {
  item: ChecklistItemDTO;
  projectId: string;
  dane: string;
  onChanged: () => void;
}) {
  const [error, setError] = useState<string | null>(null);

  // Sprint Pre-Demo: telemetría del upload. Guardamos `file` + `startedAt`
  // en un ref para poder calcular duration_ms y leer size/mime desde los
  // callbacks de la mutation sin re-cerrarlos en cada render.
  const uploadRef = useRef<{ file: File | null; startedAt: number }>({
    file: null,
    startedAt: 0,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useMutation({
    mutationFn: (file: File) =>
      uploadProjectDocument({
        projectId,
        file,
        dane,
        checklistItemId: item.id,
      }),
    onSuccess: () => {
      setError(null);
      const tracked = uploadRef.current.file;
      if (tracked) {
        track({
          event_name: "expediente_document_uploaded",
          properties: {
            project_id: projectId,
            checklist_item_id: item.id,
            file_size_bytes: tracked.size,
            mime_type: tracked.type,
            success: true,
            duration_ms: Date.now() - uploadRef.current.startedAt,
          },
        });
      }
      onChanged();
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : "Error al subir documento.";
      setError(message);
      const tracked = uploadRef.current.file;
      if (tracked) {
        track({
          event_name: "expediente_document_uploaded",
          properties: {
            project_id: projectId,
            checklist_item_id: item.id,
            file_size_bytes: tracked.size,
            mime_type: tracked.type,
            success: false,
            duration_ms: Date.now() - uploadRef.current.startedAt,
            error_message: message,
          },
        });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      deleteProjectDocument({
        documentId: item.linked_document_id!,
        dane,
      }),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Error al borrar."),
  });

  // Issue #550: la descarga va por `fetch` autenticado, no por `<a href>`.
  // El endpoint exige `Authorization` y una navegación del browser no lo
  // lleva: respondía 401 antes del 302 a la signed URL.
  const downloadMutation = useMutation({
    mutationFn: () =>
      downloadProjectDocument({
        documentId: item.linked_document_id!,
        dane,
        filename: item.linked_document_filename!,
      }),
    onSuccess: () => setError(null),
    onError: (err) =>
      setError(
        err instanceof Error
          ? err.message
          : "No pudimos descargar el documento. Intenta de nuevo en unos segundos.",
      ),
  });

  // Si ya hay doc adjunto: mostramos filename + download + delete.
  if (item.linked_document_id && item.linked_document_filename) {
    const deleting = deleteMutation.isPending;
    const downloading = downloadMutation.isPending;
    return (
      <div
        className={
          "mt-2 flex items-center gap-2 text-xs transition " +
          (deleting ? "pointer-events-none opacity-60" : "")
        }
        aria-busy={deleting}
        data-testid="checklist-doc-display"
      >
        <button
          type="button"
          onClick={() => downloadMutation.mutate()}
          disabled={downloading}
          aria-busy={downloading}
          title={downloading ? "Descargando…" : "Descargar documento"}
          className="inline-flex items-center gap-1 rounded-md bg-brand-50 px-2 py-1 font-semibold text-brand-700 hover:bg-brand-100 disabled:cursor-wait disabled:opacity-60"
          data-testid="checklist-doc-link"
        >
          {downloading ? <InlineSpinner tone="brand" /> : "📄"} {item.linked_document_filename}
        </button>
        <button
          type="button"
          onClick={() => deleteMutation.mutate()}
          disabled={deleting}
          className="text-slate-500 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label={deleting ? "Eliminando documento…" : "Quitar documento"}
          data-testid="checklist-doc-delete"
        >
          {deleting ? (
            // Spinner pequeño durante la eliminación (Sprint 11
            // follow-up #2). Sin esto, el click parecía "muerto"
            // varios segundos.
            <span
              aria-hidden="true"
              className="inline-block h-3 w-3 animate-spin rounded-full border border-slate-300 border-t-slate-600"
            />
          ) : (
            "✘"
          )}
        </button>
        {deleting && <span className="text-slate-500">Eliminando…</span>}
        {error && !deleting && <span className="text-red-600">{error}</span>}
      </div>
    );
  }

  // Sin doc: input file inline con estados visuales (uploading / error / idle).
  // Sprint Pre-Demo Alcaldesa Fase 1 — Task 2: feedback rico durante el
  // upload. Antes solo había texto "Subiendo…" plano y conexiones lentas
  // hacían pensar al funcionario que el sistema se había colgado.
  const uploading = uploadMutation.isPending;
  const startUpload = (file: File) => {
    uploadRef.current = { file, startedAt: Date.now() };
    uploadMutation.mutate(file);
  };
  // Issue #879: un 413 (tamaño) o un 415 (formato) es el archivo, no la
  // conexión: volver a mandarlo falla igual, así que se ofrece elegir otro.
  // Red caída o 5xx sí se arreglan reintentando el mismo archivo.
  const uploadError = uploadMutation.error;
  const fileRejected =
    uploadError instanceof ApiError && (uploadError.status === 413 || uploadError.status === 415);
  return (
    <div className="mt-2 flex flex-col gap-1.5 text-xs" aria-busy={uploading}>
      <label
        className={
          "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 font-semibold " +
          (uploading
            ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500"
            : "cursor-pointer border-slate-300 bg-white text-slate-700 hover:bg-slate-50")
        }
        data-testid="checklist-doc-upload-label"
      >
        {uploading ? (
          <>
            <InlineSpinner tone="brand" />
            <span>Subiendo... esto puede tomar unos segundos</span>
          </>
        ) : (
          <span>+ Adjuntar</span>
        )}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          disabled={uploading}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) startUpload(file);
            // Reset input para que vuelva a triggear si el usuario sube
            // el mismo archivo de nuevo tras un error.
            e.target.value = "";
          }}
        />
      </label>

      {uploading && (
        <div
          data-testid="upload-progress-bar"
          aria-hidden="true"
          className="h-1 w-full overflow-hidden rounded-full bg-slate-200"
        >
          <div className="h-full w-1/2 animate-pulse bg-brand-500" />
        </div>
      )}

      {error && !uploading && (
        <p className="mt-1 text-red-600">
          <span>{error}</span>{" "}
          {fileRejected ? (
            <button
              type="button"
              className="underline hover:text-red-700"
              onClick={() => fileInputRef.current?.click()}
            >
              Elegir otro archivo
            </button>
          ) : (
            <button
              type="button"
              className="underline hover:text-red-700"
              onClick={() => {
                const file = uploadRef.current.file;
                if (file) startUpload(file);
              }}
            >
              Reintentar
            </button>
          )}
        </p>
      )}
    </div>
  );
}
